import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserPlus, KeyRound, Trash2, ShieldCheck } from "lucide-react";

const ROLE_META = {
  master: { label: "Master", cls: "bg-fuchsia-500/20 text-fuchsia-300" },
  admin: { label: "Admin Manager", cls: "bg-blue-500/20 text-blue-300" },
  agent: { label: "Agent", cls: "bg-emerald-500/20 text-emerald-300" },
};

export default function Team() {
  const { user } = useAuth();
  const isMaster = user?.role === "master";
  const [team, setTeam] = useState([]);
  const [agentForm, setAgentForm] = useState({ name: "", email: "", code: "" });
  const [adminForm, setAdminForm] = useState({ name: "", email: "", password: "", code: "" });
  const [addAgent, setAddAgent] = useState(false);
  const [addAdmin, setAddAdmin] = useState(false);
  const [codeFor, setCodeFor] = useState(null);
  const [newCode, setNewCode] = useState("");

  const [activity, setActivity] = useState({});
  const load = () => {
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
    api.get("/agents/activity").then((r) => {
      const map = {};
      r.data.forEach((a) => { map[a.id] = a; });
      setActivity(map);
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const createAgent = async () => {
    if (!agentForm.name.trim() || !agentForm.code.trim()) return toast.error("Name and code are required");
    try { await api.post("/team/agents", agentForm); toast.success("Agent created"); setAddAgent(false); setAgentForm({ name: "", email: "", code: "" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const createAdmin = async () => {
    if (!adminForm.name.trim() || !adminForm.email.trim() || !adminForm.password) return toast.error("Name, email and password are required");
    try { await api.post("/team/admins", adminForm); toast.success("Admin manager created"); setAddAdmin(false); setAdminForm({ name: "", email: "", password: "", code: "" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveCode = async () => {
    if (!newCode.trim()) return toast.error("Enter a code");
    try { await api.put(`/team/${codeFor.id}/code`, { code: newCode }); toast.success("Code updated"); setCodeFor(null); setNewCode(""); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name}? This cannot be undone.`)) return;
    try { await api.delete(`/team/${u.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const canEditCode = (u) => isMaster || u.role === "agent";

  return (
    <div className="animate-fade-up" data-testid="team-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase text-primary font-bold mb-2">Access Control</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-none">Team &amp; Codes</h1>
          <p className="text-muted-foreground mt-3 text-sm max-w-lg">Create agents, hand out login codes, and control who can do what. Agents only see clients assigned to them and can scan documents, match tradelines, and build entities.</p>
        </div>
        <div className="flex gap-2">
          <Button className="rounded-lg" onClick={() => setAddAgent(true)} data-testid="add-agent-btn"><UserPlus className="h-4 w-4 mr-1" /> Add Agent</Button>
          {isMaster && <Button variant="secondary" className="rounded-lg" onClick={() => setAddAdmin(true)} data-testid="add-admin-btn"><ShieldCheck className="h-4 w-4 mr-1" /> Add Admin Manager</Button>}
        </div>
      </div>

      <div className="grid gap-3">
        {team.map((u) => (
          <Card key={u.id} className="p-4 bg-card/60 border-border flex items-center justify-between gap-4" data-testid={`team-row-${u.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold shrink-0">{u.name?.[0]?.toUpperCase() || "?"}</div>
              <div className="min-w-0">
                <div className="font-medium flex items-center gap-2 flex-wrap">{u.name || "—"} <Badge className={ROLE_META[u.role]?.cls}>{ROLE_META[u.role]?.label}</Badge></div>
                <div className="text-xs text-muted-foreground truncate">{u.email || "No email"} · {u.has_code ? "Code set" : "No code"}</div>
                {u.role === "agent" && activity[u.id] && (
                  <div className="flex gap-3 mt-1.5 text-[11px] text-muted-foreground" data-testid={`agent-activity-${u.id}`}>
                    <span><b className="text-white">{activity[u.id].client_count}</b> clients</span>
                    <span><b className="text-white">{activity[u.id].scan_count}</b> scans</span>
                    <span><b className="text-white">{activity[u.id].doc_count}</b> docs</span>
                    <span>{activity[u.id].last_active ? `active ${new Date(activity[u.id].last_active).toLocaleDateString()}` : "no activity"}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {canEditCode(u) && <Button size="sm" variant="secondary" className="rounded-lg" onClick={() => { setCodeFor(u); setNewCode(""); }} data-testid={`set-code-${u.id}`}><KeyRound className="h-3.5 w-3.5 mr-1" /> Code</Button>}
              {isMaster && u.role !== "master" && <Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(u)} data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          </Card>
        ))}
        {!team.length && <p className="text-sm text-muted-foreground py-12 text-center">No team members yet.</p>}
      </div>

      <Dialog open={addAgent} onOpenChange={setAddAgent}>
        <DialogContent className="bg-card/95 backdrop-blur-xl border-border">
          <DialogHeader><DialogTitle className="font-display">New Agent</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Name</Label><Input value={agentForm.name} onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })} data-testid="agent-name" /></div>
            <div className="space-y-1"><Label>Email (optional)</Label><Input value={agentForm.email} onChange={(e) => setAgentForm({ ...agentForm, email: e.target.value })} data-testid="agent-email" /></div>
            <div className="space-y-1"><Label>Login Code</Label><Input value={agentForm.code} onChange={(e) => setAgentForm({ ...agentForm, code: e.target.value })} className="font-mono tracking-widest" placeholder="e.g. 5001" data-testid="agent-code" /></div>
          </div>
          <DialogFooter><Button onClick={createAgent} className="rounded-lg" data-testid="save-agent-btn">Create Agent</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addAdmin} onOpenChange={setAddAdmin}>
        <DialogContent className="bg-card/95 backdrop-blur-xl border-border">
          <DialogHeader><DialogTitle className="font-display">New Admin Manager</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Name</Label><Input value={adminForm.name} onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })} data-testid="admin-name" /></div>
            <div className="space-y-1"><Label>Email</Label><Input value={adminForm.email} onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })} data-testid="admin-email" /></div>
            <div className="space-y-1"><Label>Password</Label><Input type="password" value={adminForm.password} onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })} data-testid="admin-password" /></div>
            <div className="space-y-1"><Label>Login Code (optional)</Label><Input value={adminForm.code} onChange={(e) => setAdminForm({ ...adminForm, code: e.target.value })} className="font-mono tracking-widest" data-testid="admin-code" /></div>
          </div>
          <DialogFooter><Button onClick={createAdmin} className="rounded-lg" data-testid="save-admin-btn">Create Admin Manager</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!codeFor} onOpenChange={(o) => !o && setCodeFor(null)}>
        <DialogContent className="bg-card/95 backdrop-blur-xl border-border">
          <DialogHeader><DialogTitle className="font-display">Set code for {codeFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-1"><Label>New Login Code</Label><Input value={newCode} onChange={(e) => setNewCode(e.target.value)} className="font-mono tracking-widest text-center text-lg" data-testid="new-code-input" /></div>
          <DialogFooter><Button onClick={saveCode} className="rounded-lg" data-testid="save-code-btn">Save Code</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
